/**
 * AI API client — Phase AI
 *
 * All fetch calls to /api/ai/* endpoints.
 * Import these in components instead of calling fetch directly.
 */

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Generic helper ──────────────────────────────────────────────────────────

async function _post(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`POST ${path} failed: ${res.status} — ${text}`);
  }
  return res.json();
}

async function _get(path) {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`GET ${path} failed: ${res.status} — ${text}`);
  }
  return res.json();
}

// ── Feedback ────────────────────────────────────────────────────────────────

/**
 * Save thumbs up/down + optional comment for an AI observation.
 * @param {string} obsId   - unique obs identifier (e.g. 'nifty-2025-04-07-fii')
 * @param {number} rating  - -1, 0, +1
 * @param {string} comment - optional text note
 * @param {string} tabSource - tab name (e.g. 'nifty', 'scanner')
 * @param {string} type    - observation type tag
 */
export async function postFeedback({ obsId, rating, comment = '', tabSource = '', type = '' }) {
  return _post('/api/ai/feedback', {
    obs_id:     obsId,
    rating,
    comment,
    tab_source: tabSource,
    type,
  });
}

// ── Reports ─────────────────────────────────────────────────────────────────

/**
 * Get recent AI reports for a tab.
 * @param {string} tab   - 'nifty'|'dashboard'|'scanner'|'journal'|'performance'
 * @param {number} days  - number of days to return (default 10)
 */
export async function getTabReports(tab, days = 10) {
  return _get(`/api/ai/reports/${tab}?days=${days}`);
}

/**
 * Get NIFTY AI data (sections + prediction history) for NiftyTab.
 */
export async function getNiftyAI(days = 30) {
  return _get(`/api/ai/nifty?days=${days}`);
}

/**
 * Get assembled summary day cards for AISummaryTab.
 */
export async function getAISummary(days = 10) {
  return _get(`/api/ai/summary?days=${days}`);
}

/**
 * Get current user preference memory.
 */
export async function getAIMemory() {
  return _get('/api/ai/memory');
}

/**
 * Get AI system status (report counts, key presence, etc.)
 */
export async function getAIStatus() {
  return _get('/api/ai/status');
}

/**
 * Get filtered news (HIGH/MEDIUM impact only).
 * @param {string} impact - 'HIGH', 'MEDIUM', or undefined for both
 * @param {number} hours  - how many hours back to look
 */
export async function getFilteredNews({ sort = 'latest', hours = 48, limit = 100 } = {}) {
  const params = new URLSearchParams({ sort, hours, limit });
  return _get(`/api/ai/news-filtered?${params}`);
}

// ── Admin actions ───────────────────────────────────────────────────────────

/**
 * Admin: trigger immediate report generation for a tab.
 * Returns immediately; poll getTabReports to see the result.
 * @param {string} tab         - tab name
 * @param {string} reportDate  - YYYY-MM-DD (defaults to today on server)
 */
export async function generateTabReport(tab, reportDate = null) {
  return _post(`/api/ai/generate/${tab}`, {
    report_date: reportDate,
  });
}

/**
 * Admin: trigger news impact classification now.
 */
export async function classifyNewsNow() {
  return _post('/api/ai/classify-news', {});
}

/**
 * Admin: get AI run log (token usage, latency, trigger for every Claude call).
 * @param {number} days  - how many days back (default 7)
 * @param {number} limit - max rows (default 200)
 */
export async function getAIRunLog(days = 7, limit = 200) {
  return _get(`/api/ai/run-log?days=${days}&limit=${limit}`);
}
